/**
 * /app/$tenantSlug/payroll/filings — G_FILE statutory filing hub.
 *
 * Converts LOCKED payroll snapshots (Companies Act §128(1)) into the
 * government-validatable upload files: EPFO ECR (#~#), ESIC monthly,
 * TDS 24Q FVU text, and Form-16 Part B. HR-admin only (same server-side
 * RBAC as every /api/v1/payroll/* route). Entity-switcher-aware — every
 * filing is for the ACTIVE legal entity (TAN/PF/ESI are per-entity).
 *
 * Tabbed by kind. Each tab shows a period grid (months for PF/ESI,
 * quarters for 24Q, the FY for Form-16) with a status chip + a Generate
 * CTA. Generate is step-up TOTP gated (the §128 write-once commit).
 */

import { useState } from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
  useLocation,
  useParams,
} from "@tanstack/react-router";
import { Download, FileText, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PayrollModuleShell } from "@/components/shared/payroll-module-shell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toApiError } from "@/lib/api";
import { useFilings, useCreateFiling } from "@/hooks/use-filings";
import {
  downloadFilingFile,
  triggerDownload,
  FILING_KIND_LABELS,
} from "@/lib/api/filings";
import type { FilingKind, FilingRun } from "@/lib/api/filings";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/payroll/filings",
)({
  component: FilingsRoute,
});

const KINDS: FilingKind[] = ["ecr", "esi_monthly", "tds_24q_fvu", "form16"];

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function FilingsRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [year, setYear] = useState(new Date().getFullYear());

  return (
    <PayrollModuleShell
      title="Statutory filings"
      description="Generate the government-validatable PF (ECR), ESI, TDS 24Q and Form-16 files from your locked payroll. Validate each file in the official utility (FVU / EPFO / ESIC) before uploading."
    >
      <div className="mb-4 flex items-end gap-3">
        <div>
          <Label htmlFor="filing-year">Year</Label>
          <Input
            id="filing-year"
            type="number"
            className="w-28"
            value={year}
            min={2017}
            max={2100}
            onChange={(e) => setYear(parseInt(e.target.value, 10) || year)}
          />
        </div>
      </div>

      <Tabs defaultValue="ecr">
        <TabsList>
          {KINDS.map((k) => (
            <TabsTrigger key={k} value={k}>
              {FILING_KIND_LABELS[k]}
            </TabsTrigger>
          ))}
        </TabsList>
        {KINDS.map((k) => (
          <TabsContent key={k} value={k}>
            <KindTab kind={k} year={year} tenantSlug={tenantSlug} />
          </TabsContent>
        ))}
      </Tabs>
    </PayrollModuleShell>
  );
}

function KindTab({
  kind,
  year,
  tenantSlug,
}: Readonly<{ kind: FilingKind; year: number; tenantSlug: string }>) {
  const q = useFilings(kind, year);
  const byIndex = new Map<number, FilingRun>();
  (q.data?.items ?? []).forEach((f) => byIndex.set(f.period_index, f));

  // Period set per kind.
  let periods: { index: number; label: string }[];
  if (kind === "ecr" || kind === "esi_monthly") {
    periods = MONTH_NAMES.map((m, i) => ({ index: i + 1, label: m }));
  } else if (kind === "tds_24q_fvu") {
    periods = [1, 2, 3, 4].map((qq) => ({ index: qq, label: `Q${qq}` }));
  } else {
    periods = [{ index: 0, label: `FY ${year}-${(year + 1) % 100}` }];
  }

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-base">
          {FILING_KIND_LABELS[kind]} — {year}
        </CardTitle>
        <CardDescription>{kindHint(kind)}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {periods.map((p) => (
            <PeriodCell
              key={p.index}
              kind={kind}
              year={year}
              index={p.index}
              label={p.label}
              filing={byIndex.get(p.index)}
              tenantSlug={tenantSlug}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PeriodCell({
  kind,
  year,
  index,
  label,
  filing,
  tenantSlug,
}: Readonly<{
  kind: FilingKind;
  year: number;
  index: number;
  label: string;
  filing?: FilingRun;
  tenantSlug: string;
}>) {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const createMut = useCreateFiling();
  const [downloading, setDownloading] = useState(false);

  async function onGenerate() {
    try {
      const created = await createMut.mutateAsync({
        kind,
        period_year: year,
        period_index: index,
      });
      toast.success("Filing generated. Download it and validate in the official utility before uploading.");
      void navigate({
        to: "/app/$tenantSlug/payroll/filings/$id",
        params: { tenantSlug, id: String(created.id) },
      });
    } catch (err) {
      const e = toApiError(err);
      if (e.error === "totp_enrollment_required") {
        toast.error("Enrol in two-factor authentication to generate filings.");
        void navigate({ to: "/2fa-setup", search: { required: 1, next: pathname } });
        return;
      }
      if (e.error === "totp_required") {
        toast.error("Two-factor verification required to generate the filing.");
        void navigate({ to: "/2fa-verify", search: { next: pathname } });
        return;
      }
      toast.error(e.message);
    }
  }

  async function onDownload() {
    if (!filing) return;
    setDownloading(true);
    try {
      const blob = await downloadFilingFile(filing.id, { download: true });
      triggerDownload(blob, `${kind}-${year}-${index}.txt`);
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="rounded-lg border p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        {filing ? (
          <Badge variant={filing.frozen ? "secondary" : "outline"}>
            {filing.frozen ? "Filed" : "Generated"}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">
            Not generated
          </Badge>
        )}
      </div>
      {filing ? (
        <div className="flex flex-col gap-1.5">
          <Link
            to="/app/$tenantSlug/payroll/filings/$id"
            params={{ tenantSlug, id: String(filing.id) }}
            className="text-xs text-primary hover:underline"
          >
            <FileText className="mr-1 inline h-3 w-3" />
            View detail
          </Link>
          {kind !== "form16" && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 text-xs"
              onClick={onDownload}
              disabled={downloading}
            >
              {downloading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Download className="h-3 w-3" />
              )}
              Download
            </Button>
          )}
        </div>
      ) : (
        <Button
          size="sm"
          variant="default"
          className="h-7 w-full gap-1 text-xs"
          onClick={onGenerate}
          disabled={createMut.isPending}
        >
          {createMut.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <ShieldCheck className="h-3 w-3" />
          )}
          Generate
        </Button>
      )}
    </div>
  );
}

function kindHint(kind: FilingKind): string {
  switch (kind) {
    case "ecr":
      return "EPFO ECR (#~#) — validate by uploading on the EPFO Unified Portal. Requires the entity's PF establishment code.";
    case "esi_monthly":
      return "ESIC monthly contribution sheet — validate via the ESIC employer portal bulk upload. Requires the entity's ESI employer code.";
    case "tds_24q_fvu":
      return "TDS Form 24Q FVU input text — run through the Protean RPU + FVU before filing. Requires TAN + at least one TDS challan for the quarter.";
    case "form16":
      return "Form 16 Part B (per employee). Part A comes from TRACES after the 24Q is processed; merge before issuing. Requires TAN.";
  }
}
